import {
  createTripDraft,
  loadPlanDraft,
  publishPlan,
  replacePlanDays,
  updatePlanChecklist,
  updateTripMeta,
} from '../store.js';
import { CHECKLIST_TEMPLATE_LIST, CHECKLIST_TEMPLATES } from '../config/checklist-templates.js';
import { datesInRange, dayCount } from '../utils/date-range.js';
import { escapeHtml } from '../utils/dom.js';
import { formatDateIT } from '../utils/format.js';
import { mapsSearchUrl } from '../utils/maps-url.js';
import { PLAN_WIZARD_STEPS, renderWizardShell } from './wizard-shared.js';

const DAY_TYPES = [
  { id: 'nature', label: 'Natura' },
  { id: 'water', label: 'Laghi / mare' },
  { id: 'city', label: 'Città' },
  { id: 'relax', label: 'Relax' },
  { id: 'transfer', label: 'Trasferimento' },
];

let state = null;

function resetState() {
  state = null;
}

function defaultDays(dateStart, dateEnd) {
  return datesInRange(dateStart, dateEnd).map((date, i) => ({
    date,
    title: `Giorno ${i + 1}`,
    day_type: 'nature',
    ritmo: '',
    order_idx: i,
    stops: [],
  }));
}

function ensureStateFromDraft(draft) {
  state = {
    tripId: draft.trip.id,
    planId: draft.plan.id,
    title: draft.trip.title,
    country: draft.trip.country,
    dateStart: draft.trip.date_start,
    dateEnd: draft.trip.date_end,
    days: draft.days.map((d) => ({
      date: d.date,
      title: d.title,
      day_type: d.day_type,
      ritmo: d.ritmo ?? '',
      order_idx: d.order_idx,
      stops: (d.stops ?? []).map((s) => ({
        name: s.name,
        mapsUrl: s.maps_url ?? '',
        carTime: s.car_time ?? '',
        description: s.description ?? '',
      })),
    })),
    checklistItems: draft.plan.checklist_items ?? [],
    checklistTemplate: 'empty',
    step: 1,
  };
}

async function initState(tripId) {
  if (tripId) {
    const draft = await loadPlanDraft(tripId);
    ensureStateFromDraft(draft);
    if (state.days.length === 0 && state.dateStart && state.dateEnd) {
      state.days = defaultDays(state.dateStart, state.dateEnd);
    }
    return;
  }
  state = {
    tripId: null,
    planId: null,
    title: '',
    country: '',
    dateStart: '',
    dateEnd: '',
    days: [],
    checklistItems: [],
    checklistTemplate: 'family',
    step: 1,
  };
}

function step1Html() {
  return `
    <form class="wizard-form" id="wizard-form">
      <h2 class="view-title">Informazioni viaggio</h2>
      <p class="wizard-hint">Crea la rotta template: potrai riusarla per più viaggi in famiglia.</p>
      <label class="field">
        <span class="field-label">Titolo</span>
        <input class="field-input" name="title" required placeholder="es. Slovenia 2026, in famiglia" value="${escapeHtml(state.title)}" />
      </label>
      <label class="field">
        <span class="field-label">Paese / destinazione</span>
        <input class="field-input" name="country" required placeholder="es. Slovenia" value="${escapeHtml(state.country)}" />
      </label>
      <div class="custom-form__row">
        <label class="field">
          <span class="field-label">Inizio</span>
          <input class="field-input" name="dateStart" type="date" required value="${escapeHtml(state.dateStart)}" />
        </label>
        <label class="field">
          <span class="field-label">Fine</span>
          <input class="field-input" name="dateEnd" type="date" required value="${escapeHtml(state.dateEnd)}" />
        </label>
      </div>
      <div class="wizard-form__actions">
        <button type="submit" class="btn-primary">Continua →</button>
      </div>
    </form>
  `;
}

function step2Html() {
  const rows = state.days
    .map(
      (day, i) => `
        <article class="wizard-day-card">
          <header class="wizard-day-card__head">
            <time datetime="${day.date}">${formatDateIT(day.date)}</time>
          </header>
          <label class="field">
            <span class="field-label">Titolo giornata</span>
            <input class="field-input wizard-day-input" data-day="${i}" data-field="title" value="${escapeHtml(day.title)}" />
          </label>
          <label class="field">
            <span class="field-label">Tipo</span>
            <select class="field-input wizard-day-input" data-day="${i}" data-field="day_type">
              ${DAY_TYPES.map(
                (t) =>
                  `<option value="${t.id}"${day.day_type === t.id ? ' selected' : ''}>${escapeHtml(t.label)}</option>`,
              ).join('')}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Ritmo (opz.)</span>
            <input class="field-input wizard-day-input" data-day="${i}" data-field="ritmo" value="${escapeHtml(day.ritmo)}" placeholder="es. Partenza tardi, ritmo lento" />
          </label>
        </article>
      `,
    )
    .join('');

  return `
    <div class="wizard-form">
      <h2 class="view-title">Struttura giorni</h2>
      <p class="wizard-hint">${state.days.length} giorni · ${formatDateIT(state.dateStart)} – ${formatDateIT(state.dateEnd)}</p>
      <div class="wizard-day-list">${rows}</div>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="button" class="btn-primary" data-wizard-next>Continua →</button>
      </div>
    </div>
  `;
}

function step3Html() {
  const sections = state.days
    .map((day, dayIdx) => {
      const stopRows = (day.stops ?? [])
        .map(
          (stop, stopIdx) => `
            <div class="wizard-stop-row" data-day="${dayIdx}" data-stop="${stopIdx}">
              <input class="field-input wizard-stop-input" data-field="name" placeholder="Nome tappa" value="${escapeHtml(stop.name)}" />
              <input class="field-input wizard-stop-input" data-field="mapsUrl" placeholder="Maps URL (opz.)" value="${escapeHtml(stop.mapsUrl)}" />
              <button type="button" class="btn-ghost btn-ghost--sm wizard-stop-remove">Rimuovi</button>
            </div>
          `,
        )
        .join('');

      return `
        <section class="wizard-stop-day">
          <h3 class="wizard-stop-day__title">${escapeHtml(day.title)} · ${formatDateIT(day.date)}</h3>
          <div class="wizard-stop-list" data-day="${dayIdx}">${stopRows}</div>
          <button type="button" class="btn-ghost btn-ghost--sm wizard-stop-add" data-day="${dayIdx}">+ Aggiungi tappa</button>
        </section>
      `;
    })
    .join('');

  return `
    <div class="wizard-form">
      <h2 class="view-title">Tappe</h2>
      <p class="wizard-hint">Aggiungi le tappe principali per ogni giorno. Potrai affinarle dopo.</p>
      ${sections}
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="button" class="btn-primary" data-wizard-next>Continua →</button>
      </div>
    </div>
  `;
}

function step4Html() {
  const templateOptions = CHECKLIST_TEMPLATE_LIST.map(
    (t) =>
      `<option value="${t.id}"${state.checklistTemplate === t.id ? ' selected' : ''}>${escapeHtml(t.label)} (${t.items.length} voci)</option>`,
  ).join('');

  const rows = state.checklistItems
    .map(
      (item, i) => `
        <li class="wizard-check-row">
          <input class="field-input wizard-check-label" data-idx="${i}" value="${escapeHtml(item.label)}" />
          <button type="button" class="btn-ghost btn-ghost--sm wizard-check-remove" data-idx="${i}">×</button>
        </li>
      `,
    )
    .join('');

  return `
    <div class="wizard-form">
      <h2 class="view-title">Checklist template</h2>
      <label class="field">
        <span class="field-label">Parti da un preset</span>
        <select class="field-input" id="checklist-template">${templateOptions}</select>
      </label>
      <ul class="wizard-check-list" id="checklist-items">${rows}</ul>
      <button type="button" class="btn-ghost btn-ghost--sm" id="checklist-add">+ Voce personalizzata</button>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="button" class="btn-primary" data-wizard-next>Continua →</button>
      </div>
    </div>
  `;
}

function step5Html() {
  const stopTotal = state.days.reduce((n, d) => n + (d.stops?.length ?? 0), 0);
  return `
    <div class="wizard-form">
      <h2 class="view-title">Riepilogo rotta</h2>
      <dl class="wizard-summary">
        <div><dt>Titolo</dt><dd>${escapeHtml(state.title)}</dd></div>
        <div><dt>Destinazione</dt><dd>${escapeHtml(state.country)}</dd></div>
        <div><dt>Date</dt><dd>${formatDateIT(state.dateStart)} – ${formatDateIT(state.dateEnd)}</dd></div>
        <div><dt>Giorni</dt><dd>${state.days.length}</dd></div>
        <div><dt>Tappe</dt><dd>${stopTotal}</dd></div>
        <div><dt>Checklist</dt><dd>${state.checklistItems.length} voci</dd></div>
      </dl>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="button" class="btn-primary" id="wizard-publish">Pubblica rotta</button>
      </div>
      <p class="wizard-hint" id="wizard-error" hidden></p>
    </div>
  `;
}

function renderBody() {
  switch (state.step) {
    case 1:
      return step1Html();
    case 2:
      return step2Html();
    case 3:
      return step3Html();
    case 4:
      return step4Html();
    case 5:
      return step5Html();
    default:
      return step1Html();
  }
}

function goPlanStep(tripId, step) {
  const base = tripId ? `#/new/plan/${tripId}` : '#/new/plan';
  location.hash = step > 1 && tripId ? `${base}/${step}` : base;
}

function bindStep1(container) {
  container.querySelector('#wizard-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const title = String(fd.get('title')).trim();
    const country = String(fd.get('country')).trim();
    const dateStart = String(fd.get('dateStart'));
    const dateEnd = String(fd.get('dateEnd'));
    if (dayCount(dateStart, dateEnd) < 1) {
      alert('La data di fine deve essere uguale o successiva all’inizio.');
      return;
    }

    state.title = title;
    state.country = country;
    state.dateStart = dateStart;
    state.dateEnd = dateEnd;
    state.days = defaultDays(dateStart, dateEnd);

    try {
      if (!state.tripId) {
        const { trip, plan } = await createTripDraft({ title, country, dateStart, dateEnd });
        state.tripId = trip.id;
        state.planId = plan.id;
        goPlanStep(trip.id, 2);
        return;
      }
      await updateTripMeta(state.tripId, { title, country, dateStart, dateEnd });
      goPlanStep(state.tripId, 2);
    } catch (err) {
      alert(err.message);
    }
  });
}

function collectDayInputs(container) {
  container.querySelectorAll('.wizard-day-input').forEach((el) => {
    const i = Number(el.dataset.day);
    const field = el.dataset.field;
    if (state.days[i]) state.days[i][field] = el.value;
  });
}

function collectStopInputs(container) {
  container.querySelectorAll('.wizard-stop-day').forEach((section) => {
    const dayIdx = Number(section.querySelector('.wizard-stop-list')?.dataset.day);
    const stops = [];
    section.querySelectorAll('.wizard-stop-row').forEach((row) => {
      const name = row.querySelector('[data-field="name"]')?.value?.trim();
      if (!name) return;
      let mapsUrl = row.querySelector('[data-field="mapsUrl"]')?.value?.trim() || '';
      if (!mapsUrl) mapsUrl = mapsSearchUrl(`${name} ${state.country}`) ?? '';
      stops.push({ name, mapsUrl, carTime: '', description: '' });
    });
    if (state.days[dayIdx]) state.days[dayIdx].stops = stops;
  });
}

function collectChecklistInputs(container) {
  const items = [];
  container.querySelectorAll('.wizard-check-label').forEach((input, i) => {
    const label = input.value.trim();
    if (!label) return;
    items.push({
      itemKey: `custom-${i}-${label.slice(0, 12).replace(/\W/g, '').toLowerCase()}`,
      label,
      category: 'altro',
    });
  });
  state.checklistItems = items;
}

function bindCommonNav(container) {
  container.querySelector('[data-wizard-back]')?.addEventListener('click', () => {
    if (state.step === 2) collectDayInputs(container);
    if (state.step === 3) collectStopInputs(container);
    if (state.step === 4) collectChecklistInputs(container);
    goPlanStep(state.tripId, state.step - 1);
  });

  container.querySelector('[data-wizard-next]')?.addEventListener('click', () => {
    if (state.step === 2) collectDayInputs(container);
    if (state.step === 3) collectStopInputs(container);
    if (state.step === 4) collectChecklistInputs(container);
    goPlanStep(state.tripId, state.step + 1);
  });
}

function bindStep2(container) {
  bindCommonNav(container);
}

function bindStep3(container, rerender) {
  container.querySelectorAll('.wizard-stop-add').forEach((btn) => {
    btn.addEventListener('click', () => {
      collectStopInputs(container);
      const dayIdx = Number(btn.dataset.day);
      state.days[dayIdx].stops.push({ name: '', mapsUrl: '', carTime: '', description: '' });
      rerender();
    });
  });

  container.querySelectorAll('.wizard-stop-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      collectStopInputs(container);
      const row = btn.closest('.wizard-stop-row');
      const dayIdx = Number(row.dataset.day);
      const stopIdx = Number(row.dataset.stop);
      state.days[dayIdx].stops.splice(stopIdx, 1);
      rerender();
    });
  });

  bindCommonNav(container);
}

function bindStep4(container, rerender) {
  container.querySelector('#checklist-template')?.addEventListener('change', (e) => {
    state.checklistTemplate = e.target.value;
    state.checklistItems = [...(CHECKLIST_TEMPLATES[state.checklistTemplate]?.items ?? [])];
    rerender();
  });

  container.querySelector('#checklist-add')?.addEventListener('click', () => {
    collectChecklistInputs(container);
    state.checklistItems.push({
      itemKey: `custom-${Date.now()}`,
      label: '',
      category: 'altro',
    });
    rerender();
  });

  container.querySelectorAll('.wizard-check-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      collectChecklistInputs(container);
      state.checklistItems.splice(Number(btn.dataset.idx), 1);
      rerender();
    });
  });

  bindCommonNav(container);
}

function bindStep5(container) {
  bindCommonNav(container);

  container.querySelector('#wizard-publish')?.addEventListener('click', async () => {
    const errEl = container.querySelector('#wizard-error');
    const btn = container.querySelector('#wizard-publish');
    btn.disabled = true;
    errEl.hidden = true;

    try {
      await updateTripMeta(state.tripId, {
        title: state.title,
        country: state.country,
        dateStart: state.dateStart,
        dateEnd: state.dateEnd,
      });
      await replacePlanDays(
        state.planId,
        state.days.map((d, i) => ({
          date: d.date,
          title: d.title,
          day_type: d.day_type,
          ritmo: d.ritmo,
          order_idx: i,
          stops: (d.stops ?? []).map((s, j) => ({
            name: s.name,
            maps_url: s.mapsUrl || mapsSearchUrl(`${s.name} ${state.country}`),
            car_time: s.carTime || null,
            description: s.description || null,
            order_idx: j,
          })),
        })),
      );
      await updatePlanChecklist(state.planId, state.checklistItems);
      await publishPlan(state.planId);
      const publishedTripId = state.tripId;
      resetState();
      location.hash = `#/trip/${publishedTripId}/start`;
    } catch (err) {
      errEl.textContent = err.message;
      errEl.hidden = false;
      btn.disabled = false;
    }
  });
}

export async function renderWizardPlan(container, { tripId = null, step = 1 } = {}) {
  if (!state || (tripId && state.tripId !== tripId)) {
    await initState(tripId);
  }
  state.step = step;

  const backHref = state.tripId ? '#/shelf' : '#/shelf';

  container.innerHTML = renderWizardShell({
    title: 'Nuova rotta',
    subtitle: state.title || 'Piano template',
    steps: PLAN_WIZARD_STEPS,
    currentStep: state.step,
    backHref,
    bodyHtml: renderBody(),
  });

  const rerender = () => renderWizardPlan(container, { tripId: state.tripId, step: state.step });

  switch (state.step) {
    case 1:
      bindStep1(container);
      break;
    case 2:
      bindStep2(container);
      break;
    case 3:
      bindStep3(container, rerender);
      break;
    case 4:
      bindStep4(container, rerender);
      break;
    case 5:
      bindStep5(container);
      break;
    default:
      break;
  }
}

export function clearWizardPlanState() {
  resetState();
}
